import '@capaj/react-quill/dist/quill.snow.css'

import { isStringUrl } from 'back-end/src/util/strings'
import {
  Box,
  ButtonIcon,
  ButtonInfo,
  Flex,
  Heading,
  Link,
  FullBlockSpinner,
  Table,
  Td,
  Text,
  Th,
  Tr
} from 'front-end/src/components'
import { IconCamera } from 'front-end/src/components/common/icon/IconCamera'
import { IconCopy } from 'front-end/src/components/common/icon/IconCopy'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import PageHeader from 'front-end/src/components/common/page/header/PageHeader'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'
import { TOPBAR_HEIGHT } from 'front-end/src/components/common/topbar/Topbar'
import { FollowResourceType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { DangerouslySetInnerHTML } from 'front-end/src/utils/DangerouslySetInnerHTML'
import queryString from 'query-string'
import React, { useEffect } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { brandColors } from 'shared/basicPalette'
import { isIE } from 'shared/helpers'

import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'

import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import {
  breakpoints,
  fontSizes
} from '../../../../components/chakraThemeExtension'
import { CommentsFooter } from '../../../../components/common/comments/comments-footer/CommentsFooter'
import { notification } from '../../../../components/common/notifications/Notifications'
import { truncateMSTeamsUrl } from '../../../../utils/url'
import DateIcon from '../date-icon/DateIcon'
import EventAttendance from '../event-attendance/EventAttendance'
import {
  useCancelAttendanceMutation,
  useChangeStatusIn3dPartyCalendarsMutation,
  useRegisterToAttendMutation
} from '../event-attendance/EventAttendance.codegen'
import StatusOfEvent, {
  getStatus,
  StatusEventStatusType
} from '../event-status/EventStatus'
import { useEventDetailQuery } from './EventDetail.codegen'
import { EventFormattedTimeTwoLine } from './EventFormattedTimeTwoLine'
import EventTopbar from './EventTopbar'
import { getTimeZoneDateFromUTCDate } from 'front-end/src/pages/manage-events/event-edit/tabs/event-detail/eventDetailUtils'
import { EventOtherFutureOccurrences } from 'front-end/src/pages/manage-events/components/event-detail/EventOtherFutureOccurrences'

export interface ISimpleUser {
  id: number
  userIdentity: string
  username: string
  image_url: string
}

export const EventDetailPage: React.FC<
  RouteComponentProps<{
    eventId: string
  }>
> = (props) => {
  const { params } = props.match
  const eventId = Number(params.eventId)

  const { data, loading, refetch } = useEventDetailQuery({
    variables: { eventId, eventIdJson: eventId },
    fetchPolicy: 'network-only'
  })

  const [cancelAttendance] = useCancelAttendanceMutation({
    variables: { eventId }
  })
  const [registerToAttend] = useRegisterToAttendMutation({
    variables: { eventId }
  })

  const [acceptInCalendars] = useChangeStatusIn3dPartyCalendarsMutation({
    variables: { eventId, attending: true }
  })
  const [declineInCalendars] = useChangeStatusIn3dPartyCalendarsMutation({
    variables: { eventId, attending: false }
  })
  const { history, location } = props

  useEffect(() => {
    let event = data?.event
    if (!event) {
      return
    }

    const resetSearch = () => {
      history.replace({
        search: ''
      })
    }

    const { currentUserInvitee } = event
    if (location.search) {
      const query = queryString.parse(location.search)

      const isConfirmingAsAttending =
        query.confirm === 'true' || query.confirm === null // the older format for confirming did not include =true

      if (currentUserInvitee) {
        if (isConfirmingAsAttending && currentUserInvitee.attending === true) {
          return resetSearch()
        }
        if (
          query.confirm === 'false' &&
          currentUserInvitee.attending === false
        ) {
          return resetSearch()
        }
      }
      ;(async () => {
        try {
          if (isConfirmingAsAttending) {
            await registerToAttend()
            acceptInCalendars()
            notification.info(t`Marked as attending`)
          } else if (location.search.includes('?confirm=false')) {
            await cancelAttendance()
            declineInCalendars()
            notification.info(t`Marked as not attending`)
          }
          refetch()
        } catch (err) {
          notification.warning(err.message)
        } finally {
          resetSearch()
        }
      })()
    }
  }, [data?.event?.id])
  if (!data) {
    return null
  }
  let event = data.event!
  let usersTimezone = data.me.timezone

  if (!event) {
    notification.warning(t`Event is not available anymore`) // it was cancelled or it was made inaccessible for the user

    return <Redirect to={staticLinks.home} />
  }

  if (loading) {
    return <FullBlockSpinner />
  }

  const DescriptionContent = () => (
    <Box mr="32px">
      <Box css={getQuillStyle('ql-editor')} className="event-summary ql-editor">
        <DangerouslySetInnerHTML content={event.description as string} />
      </Box>
    </Box>
  )

  const eventLocation = event.location
  const googleMeetUrl = `meet.google.com/${event?.google_meet_id}`
  const eventDayjsStartTime = getTimeZoneDateFromUTCDate({
    date: event.start_time,
    timezone: usersTimezone
  })

  return (
    <>
      <EventTopbar title={event.title} eventId={event.id} />
      <Box
        className="Page"
        bg={brandColors.white}
        borderTop={`${TOPBAR_HEIGHT} solid`}
        tabIndex={-1}
      >
        <Box className="Page__inner">
          <PageHeader
            fullWidth
            className="PageHeader"
            as="div"
            showBackButton={false}
            title={
              <Flex alignItems="center">
                {eventDayjsStartTime && (
                  <DateIcon fontWeight="400" date={eventDayjsStartTime} />
                )}
                <Flex direction="column" ml="16px">
                  <Heading as="h1">{event.title}</Heading>
                </Flex>
              </Flex>
            }
            controls={<StatusOfEvent event={event} />}
          />

          <Flex p="32px 16px 16px 16px" direction={['column', 'column', 'row']}>
            <Flex grow={1} direction="column">
              {event.recurringEvent && (
                <Box fontSize={fontSizes.lg} mb="16px">
                  <Trans>This event is part of</Trans>{' '}
                  <Link
                    ml="6px"
                    to={linkBuilder.eventDetailSeries(event.recurringEvent.id)}
                    css={css`
                      &:hover {
                        text-decoration: none;
                        .chakra-text {
                          text-decoration: underline;
                        }
                      }
                    `}
                  >
                    <Flex display="inline-flex" alignItems="baseline">
                      <IconRefresh boxSize="16px" mr="4px" />
                      <Text>{event.recurringEvent.title}</Text>
                    </Flex>
                  </Link>
                </Box>
              )}
              <Table
                variant="unstyled"
                fontSize={fontSizes.lg}
                color={brandColors.black}
                css={css`
                  @media (max-width: ${breakpoints.sm}) {
                    th,
                    td {
                      display: block;
                    }
                  }
                `}
              >
                <Tr>
                  <Th pl="0px" color={brandColors.grey[500]}>
                    <Trans>When</Trans>:
                  </Th>
                  <Td>
                    <EventFormattedTimeTwoLine
                      event={event}
                      timezone={!isIE ? usersTimezone : null} // Converting to users timezone didn't work on IE11 = do not user timezone
                    />
                  </Td>
                </Tr>

                {event?.google_meet_id && (
                  <Tr>
                    <Th pl="0px" color={brandColors.grey[500]}>
                      <Trans>Conference</Trans>:
                    </Th>
                    <Td>
                      <Flex alignItems="center" flexWrap="wrap">
                        <ButtonInfo
                          px="12px"
                          onClick={() =>
                            // @ts-expect-error
                            window
                              .open(`https://${googleMeetUrl}`, '_blank')
                              .focus()
                          }
                        >
                          <IconCamera boxSize="18px" />
                          <Trans>Join with Google Meet</Trans>
                        </ButtonInfo>
                        <Box ml="16px">
                          <CopyToClipboard
                            text={googleMeetUrl}
                            onCopy={() => {
                              notification.info(
                                t`Google meet was copied to clipboard`
                              )
                            }}
                          >
                            <ButtonIcon aria-label={`Copy google meet link`}>
                              <IconCopy
                                color={brandColors.grey.mid}
                                boxSize="18px"
                              />
                            </ButtonIcon>
                          </CopyToClipboard>
                        </Box>
                        <Text
                          mt="5px"
                          flex="0 0 100%"
                          color={brandColors.grey.mid}
                        >
                          {googleMeetUrl}
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                )}

                {event?.ms_teams_enabled && event?.ms_teams_url && (
                  <Tr>
                    <Th pl="0px" color={brandColors.grey[500]}>
                      <Trans>Conference</Trans>:
                    </Th>
                    <Td>
                      <Flex alignItems="center" flexWrap="wrap">
                        <ButtonInfo
                          px="12px"
                          onClick={() =>
                            // @ts-expect-error
                            window.open(event?.ms_teams_url, '_blank').focus()
                          }
                        >
                          <IconCamera boxSize="18px" />
                          <Trans>Join with Microsoft Teams</Trans>
                        </ButtonInfo>
                        <Box ml="16px">
                          <CopyToClipboard
                            text={event?.ms_teams_url}
                            onCopy={() => {
                              notification.info(
                                t`Microsoft Teams meeting link was copied to clipboard`
                              )
                            }}
                          >
                            <ButtonIcon
                              aria-label={`Copy Microsoft Teams link`}
                            >
                              <IconCopy
                                color={brandColors.grey.mid}
                                boxSize="18px"
                              />
                            </ButtonIcon>
                          </CopyToClipboard>
                        </Box>
                        <Text
                          mt="5px"
                          flex="0 0 100%"
                          color={brandColors.grey.mid}
                        >
                          {truncateMSTeamsUrl(event?.ms_teams_url)}
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                )}
                {!!eventLocation && (
                  <Tr>
                    <Th pl="0px" color={brandColors.grey[500]}>
                      <Trans>Where</Trans>:
                    </Th>
                    <Td>
                      {isStringUrl(eventLocation) ? (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={eventLocation}
                        >
                          {eventLocation}
                        </a>
                      ) : (
                        eventLocation
                      )}
                    </Td>
                  </Tr>
                )}
                <Tr>
                  <Th pl="0px" color={brandColors.grey[500]}>
                    <Trans>Created by</Trans>:
                  </Th>
                  <Td>{event.author.display_name}</Td>
                </Tr>
              </Table>
              {event.description && (
                <Box mt="24px">
                  <DescriptionContent />
                </Box>
              )}
            </Flex>
            <Box
              minW={[0, 0, '300px']}
              pl="32px"
              maxW="400px"
              mt={['48px', '48px', 0]}
              borderLeft={[
                'none',
                'none',
                `1px solid ${brandColors.grey[200]}`
              ]}
            >
              <EventAttendance
                event={event}
                currentUserInvitee={event?.currentUserInvitee}
                fetchEvent={refetch}
              />
              {event.recurringEvent?.events && (
                <EventOtherFutureOccurrences
                  events={event.recurringEvent?.events}
                  eventClosed={
                    getStatus(event) === StatusEventStatusType.COMPLETED
                  }
                />
              )}
            </Box>
          </Flex>
        </Box>

        {event && (
          <Box bg={brandColors.grey[75]}>
            <Box pt="48px" className="Page__inner">
              <Flex
                justifyContent="center"
                css={css`
                  .comments {
                    margin-top: 16px;
                    max-width: 700px;
                    flex-grow: 1;
                  }
                `}
              >
                <CommentsFooter
                  itemType={FollowResourceType.event}
                  itemId={event.id}
                  allowComments={event.allow_comments}
                />
              </Flex>
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}
